import React, { useContext, useState, useEffect } from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import ThemeToggleButton from "../../assets/css/themeToggleButton";
import MenuIcon from "@mui/icons-material/Menu";
import { useIsMobile } from "../../util/utils";
import Icon from "../../assets/images/plany365_withtext.ico";
import Logo from "../../assets/images/plany365.ico";
import { useSelectedBusiness } from "../../redux/store";
import { CREATE_BUSINESS_PROFILE } from "../../util/constants";

const drawerWidth = 200;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
const Content = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  marginTop: "5px",
  minWidth: 0,
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
}));

interface PortalProps {
  TabComponent: React.ComponentType<any>;
  title: string;
  props: any;
}
const Portal: React.FC<PortalProps> = ({ TabComponent, props }) => {
  const navigate = useNavigate();
  const selectedBusiness = useSelectedBusiness();
  const hasBusiness = Boolean(selectedBusiness?.businessBasic?.url);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Inside your BusinessForm component:
  useEffect(() => {
    console.log("🔁 BusinessForm MOUNTED");
    return () => console.log("🧹 BusinessForm UNMOUNTED");
  }, []); // Empty dependency array

  const handleDrawerToggle = () => setOpen((o) => !o);
  const handleMobileToggle = () => setMobileOpen((o) => !o);
  const handleNavigation = (
    path: string,
    event: React.MouseEvent,
    mobile = false
  ) => {
    navigate(path, { state: { from: window.location.href } });
    mobile ? setMobileOpen(false) : setOpen(false);
  };
  const getTooltipText = () =>
    isMobile ? (mobileOpen ? "Close" : "Expand") : open ? "Collapse" : "Expand";
  const getDrawerIcon = () =>
    open ? (
      <ChevronLeftIcon />
    ) : (
      <img
        src={Logo}
        alt="Logo"
        style={{ width: 50, height: 50, marginLeft: 10 }}
      />
    );

  const listItemTextStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const mainListItems = (
    <List sx={{ flexGrow: 1 }}>
      {/* Calendar */}
      <Tooltip
        title={hasBusiness ? "Calendar" : CREATE_BUSINESS_PROFILE}
        placement="right"
      >
        <span>
          <ListItem
            button
            disabled={!hasBusiness}
            onClick={(e) => handleNavigation("/biz/calendar", e)}
          >
            <ListItemIcon>
              <DateRangeIcon />
            </ListItemIcon>
            <ListItemText primary="Calendar" sx={listItemTextStyle} />
          </ListItem>
        </span>
      </Tooltip>
      {/* Booking Types */}
      <Tooltip
        title={hasBusiness ? "Booking type" : CREATE_BUSINESS_PROFILE}
        placement="right"
      >
        <span>
          <ListItem
            button
            disabled={!hasBusiness}
            onClick={(e) => handleNavigation("/biz/bookingtypes", e)}
          >
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText primary="Booking type" sx={listItemTextStyle} />
          </ListItem>
        </span>
      </Tooltip>
      {/* Staff */}
      <Tooltip
        title={hasBusiness ? "Staff member" : CREATE_BUSINESS_PROFILE}
        placement="right"
      >
        <span>
          <ListItem
            button
            disabled={!hasBusiness}
            onClick={(e) => handleNavigation("/biz/staff", e)}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Staff member" sx={listItemTextStyle} />
          </ListItem>
        </span>
      </Tooltip>
      {/* My Business */}
      <ListItem
        button
        onClick={(e) => handleNavigation("/biz/business", e, isMobile)}
      >
        <Tooltip title="My business" placement="right">
          <ListItemIcon>
            <BusinessIcon />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary="My business" sx={listItemTextStyle} />
      </ListItem>
    </List>
  );

  const bottomListItems = (
    <List>
      <Divider />
      <ListItem button onClick={(e) => handleNavigation("/biz/pricing", e)}>
        <Tooltip title="Upgrade Subscription" placement="right">
          <ListItemIcon>
            <UpgradeIcon />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary="Upgrade Subscription" sx={listItemTextStyle} />
      </ListItem>
      <ListItem button>
        <Tooltip title="Change Theme" placement="right">
          <ListItemIcon>
            <ThemeToggleButton />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary="Change Theme" sx={listItemTextStyle} />
      </ListItem>
    </List>
  );

  const drawer = (
    <>
      <DrawerHeader>
        {open && (
          <img
            src={Icon}
            alt="Logo"
            style={{ width: 100, height: 20, marginRight: 50 }}
            onClick={handleDrawerToggle}
          />
        )}
        <Tooltip title={getTooltipText()} placement="right">
          <IconButton
            onClick={isMobile ? handleMobileToggle : handleDrawerToggle}
          >
            {getDrawerIcon()}
          </IconButton>
        </Tooltip>
      </DrawerHeader>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {mainListItems}
        {bottomListItems}
      </Box>
    </>
  );

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleMobileToggle}
            sx={{
              position: "fixed",
              top:
                location.pathname === "/profile"
                  ? theme.spacing(2.5)
                  : theme.spacing(1),
              left: theme.spacing(1),
              zIndex: 1100,
              color: theme.palette.primary.contrastText,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {!isMobile ? (
          <Drawer variant="permanent" open={open}>
            {drawer}
          </Drawer>
        ) : (
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleMobileToggle}
            ModalProps={{ keepMounted: true }}
          >
            {drawer}
          </MuiDrawer>
        )}
        <Content>
          <TabComponent {...props} />
        </Content>
      </Box>
    </>
  );
};

export default Portal;
